import React, { useState } from 'react';
import {
  User,
  Mail,
  Phone,
  Briefcase,
  Building2,
  Clock3,
  Pencil,
} from 'lucide-react';
import { useSelector, useDispatch } from 'react-redux';
import { editProfile } from '../../api/api';
import { fetchMeThunk } from '../../redux/slices/authSlice';
import { toast } from 'react-hot-toast';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';

const GeneralTab = ({ isDarkMode }) => {
  const { user } = useSelector((state) => state.auth);
  const dispatch = useDispatch();

  const [formData, setFormData] = useState({
    name: user?.name || '',
    phone_number: user?.phone_number || '',
    company_name: user?.company_name || '',
    job_title: user?.job_title || '',
  });

  const [loading, setLoading] = useState(false);

  // Separate edit states per card
  const [profileEditMode, setProfileEditMode] = useState(false);
  const [orgEditMode, setOrgEditMode] = useState(false);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async (section) => {
    try {
      if (section === 'profile' && !formData.name.trim()) {
        toast.error('Name is required');
        return;
      }
      setLoading(true);
      const res = await editProfile({ userId: user.id, ...formData });
      toast.success(res?.message || 'Profile updated successfully');
      await dispatch(fetchMeThunk());

      if (section === 'profile') setProfileEditMode(false);
      if (section === 'org') setOrgEditMode(false);
    } catch (err) {
      toast.error(err.message || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = (section) => {
    // Reset to original user data
    setFormData({
      name: user?.name || '',
      phone_number: user?.phone_number || '',
      company_name: user?.company_name || '',
      job_title: user?.job_title || '',
    });
    if (section === 'profile') setProfileEditMode(false);
    if (section === 'org') setOrgEditMode(false);
  };

  return (
    <div className="space-y-4">
      {/* <div className="grid grid-cols-1 md:grid-cols-3 gap-6"> */}
      <div
        className={`p-3 rounded-lg border h-full ${
          isDarkMode
            ? 'bg-gray-700 border-gray-600 text-white'
            : 'bg-white text-gray-800'
        }`}
      >
        <h3 className="text-sm font-semibold mb-4 flex items-center justify-between">
          <span className="flex items-center">
            <span className="bg-brand/10 text-brand p-1 rounded-md mr-2">
              <User className="w-5 h-5" />
            </span>
            Profile Information
          </span>
          <button
            type="button"
            onClick={() => setProfileEditMode((prev) => !prev)}
            className="text-gray-400 hover:text-gray-600"
          >
            <Pencil className="w-4 h-4" />
          </button>
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-2 text-sm">
          {/* Editable Name */}
          <div>
            <label className="flex items-center mb-1 text-gray-500 dark:text-gray-400">
              <User className="w-4 h-4 mr-2 text-gray-400" />
              Name
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              disabled={!profileEditMode}
              className={`border rounded-md px-3 py-2 w-full focus:outline-none focus:ring 
                  ${
                    isDarkMode
                      ? 'bg-gray-800 border-gray-500 text-white'
                      : 'bg-white border-gray-300 text-gray-800'
                  }
                  ${!profileEditMode ? 'opacity-60 cursor-not-allowed' : ''}`}
            />
          </div>

          {/* Email (read-only) */}
          <div>
            <label className="flex items-center mb-1 text-gray-500 dark:text-gray-400">
              <Mail className="w-4 h-4 mr-2 text-gray-400" />
              Email Address
            </label>
            <input
              type="email"
              value={user?.email || ''}
              disabled
              className={`border rounded-md px-3 py-2 w-full opacity-70 cursor-not-allowed select-none
        ${
          isDarkMode
            ? 'bg-gray-800 border-gray-600 text-gray-300'
            : 'bg-gray-100 border-gray-300 text-gray-600'
        }`}
            />
          </div>
          {/* Editable Phone */}
          <div className="w-full">
            <label className="flex items-center mb-1 text-gray-500 dark:text-gray-400">
              <Phone className="w-4 h-4 mr-2 text-gray-400" />
              Phone Number
            </label>
            <div className="w-full">
              <PhoneInput
                country={'us'}
                value={formData.phone_number}
                onChange={(value) => handleChange('phone_number', value)}
                disabled={!profileEditMode}
                containerClass="w-full"
                inputClass={`!w-full !text-sm !py-2 !pl-[48px] !border rounded-md focus:outline-none focus:ring
        ${
          isDarkMode
            ? '!bg-gray-800 !border-gray-500 !text-white'
            : '!bg-white !border-gray-300 !text-gray-800'
        }
        ${!profileEditMode ? '!opacity-60 !cursor-not-allowed' : ''}
      `}
                buttonClass={`!border-r ${
                  isDarkMode
                    ? '!bg-gray-700 !border-gray-500'
                    : '!bg-gray-100 !border-gray-300'
                }
      ${!profileEditMode ? '!opacity-60 !cursor-not-allowed' : ''}`}
              />
            </div>
          </div>
        </div>

        {profileEditMode && (
          <div className="flex justify-end space-x-3 mt-4">
            <button
              onClick={() => handleCancel('profile')}
              className={`px-4 py-2 rounded-md text-sm border 
                  ${
                    isDarkMode
                      ? 'border-gray-500 text-gray-300 hover:bg-gray-600'
                      : 'border-gray-300 text-gray-600 hover:bg-gray-100'
                  }`}
            >
              Cancel
            </button>
            <button
              onClick={() => handleSave('profile')}
              disabled={loading}
              className="px-4 py-2 rounded-md text-sm bg-brand text-white hover:bg-indigo-700 disabled:opacity-60"
            >
              {loading ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        )}
      </div>

      {/* Organization Information Card */}
      <div
        className={`p-3 rounded-lg border ${
          isDarkMode
            ? 'bg-gray-700 border-gray-600 text-white'
            : 'bg-white text-gray-800'
        }`}
      >
        <h3 className="text-sm font-semibold mb-4 flex items-center justify-between">
          <span className="flex items-center">
            <span className="bg-brand/10 text-brand p-1 rounded-md mr-2">
              <Building2 className="w-5 h-5" />
            </span>
            Organization Information
          </span>

          <button
            type="button"
            onClick={() => setOrgEditMode((prev) => !prev)}
            className="text-gray-400 hover:text-gray-600"
          >
            <Pencil className="w-4 h-4" />
          </button>
        </h3>

        {/* ⭐ Make fields align in 2 columns */}
        <div className="grid grid-cols-1 md:grid-cols-2 p-2 gap-4 text-sm">
          {/* Company */}
          <div>
            <label className="flex items-center mb-1 text-gray-500 dark:text-gray-400">
              <Building2 className="w-4 h-4 mr-2 text-gray-400" />
              Company Name
            </label>
            <input
              type="text"
              value={formData.company_name}
              onChange={(e) => handleChange('company_name', e.target.value)}
              disabled={!orgEditMode}
              className={`border rounded-md px-3 py-2 w-full ${
                isDarkMode
                  ? 'bg-gray-800 border-gray-500 text-white'
                  : 'bg-white border-gray-300 text-gray-800'
              } ${!orgEditMode ? 'opacity-60 cursor-not-allowed' : ''}`}
            />
          </div>

          {/* Job Title */}
          <div>
            <label className="flex items-center mb-1 text-gray-500 dark:text-gray-400">
              <Briefcase className="w-4 h-4 mr-2 text-gray-400" />
              Job Title
            </label>
            <input
              type="text"
              value={formData.job_title}
              onChange={(e) => handleChange('job_title', e.target.value)}
              disabled={!orgEditMode}
              className={`border rounded-md px-3 py-2 w-full ${
                isDarkMode
                  ? 'bg-gray-800 border-gray-500 text-white'
                  : 'bg-white border-gray-300 text-gray-800'
              } ${!orgEditMode ? 'opacity-60 cursor-not-allowed' : ''}`}
            />
          </div>
        </div>

        {orgEditMode && (
          <div className="flex justify-end space-x-3 mt-4">
            <button
              onClick={() => handleCancel('org')}
              className={`px-4 py-2 rounded-md text-sm border ${
                isDarkMode
                  ? 'border-gray-500 text-gray-300 hover:bg-gray-600'
                  : 'border-gray-300 text-gray-600 hover:bg-gray-100'
              }`}
            >
              Cancel
            </button>

            <button
              onClick={() => handleSave('org')}
              disabled={loading}
              className="px-4 py-2 rounded-md text-sm bg-brand text-white hover:bg-indigo-700 disabled:opacity-60"
            >
              {loading ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        )}
      </div>

      {/* Timezone (static) */}
      <div
        className={`p-3 rounded-lg border h-full ${
          isDarkMode
            ? 'bg-gray-700 border-gray-600 text-white'
            : 'bg-white text-gray-800'
        }`}
      >
        <h3 className="text-sm font-semibold mb-2 flex items-center">
          <span className="bg-brand/10 text-brand p-1 rounded-md mr-2">
            <Clock3 className="w-5 h-5" />
          </span>
          Timezone Settings
        </h3>
        <p
          className={`text-xs mb-4 ${
            isDarkMode ? 'text-gray-400' : 'text-gray-500'
          }`}
        >
          Set your preferred timezone for scheduling and notifications.
        </p>
        <div className="text-sm p-2">
          <span className="flex items-center font-medium text-gray-500 dark:text-gray-400">
            <Clock3 className="w-4 h-4 mr-1 text-gray-400" />
            Timezone
          </span>
          <div
            className={`mt-1 font-medium ${
              isDarkMode ? 'text-white' : 'text-gray-800'
            }`}
          >
            Pacific Time (US & Canada)
          </div>
        </div>
      </div>
    </div>
    // </div>
  );
};

export default GeneralTab;
